import { useState, useEffect, useRef } from 'react'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useNotification } from '@refinedev/core'
import {
  Mail, MessageCircle, Send, Loader2, CheckCircle2, XCircle,
  ChevronDown, Info,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type SendStatus = 'idle' | 'sending' | 'success' | 'error'

function StatusBanner({ status, message }: { status: SendStatus; message: string }) {
  if (status === 'idle' || status === 'sending') return null
  return (
    <div className={cn(
      'flex items-start gap-2 p-3 rounded-xl border text-xs',
      status === 'success'
        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
        : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
    )}>
      {status === 'success'
        ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
        : <XCircle size={14} className="shrink-0 mt-0.5" />}
      <span>{message}</span>
    </div>
  )
}

function EmailPanel() {
  const [recipient, setRecipient] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [status, setStatus] = useState<SendStatus>('idle')
  const [statusMsg, setStatusMsg] = useState('')
  const { open: notify } = useNotification()

  const handleSend = async () => {
    if (!recipient || !subject || !body) {
      notify?.({ type: 'error', message: 'recipient_email, subject, and body are all required.' })
      return
    }
    setStatus('sending')
    try {
      await flaskApi.post('/messaging/email', { recipient_email: recipient, subject, body })
      setStatus('success')
      setStatusMsg(`Email queued for ${recipient}`)
      setRecipient(''); setSubject(''); setBody('')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Failed to queue email'
      setStatus('error')
      setStatusMsg(msg)
    }
  }

  return (
    <div className="bento-card p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#076698]/10 text-[#076698] dark:bg-sky-900/30 dark:text-sky-300 flex items-center justify-center">
          <Mail size={16} />
        </div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-700 dark:text-slate-200">Send Email</h2>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Queue a one-off email to any recipient</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Recipient Email *</label>
          <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="recipient@example.com" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Subject *</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Body *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message here…"
            rows={5}
            className="w-full px-3 py-2 text-sm form-field resize-y"
          />
        </div>
      </div>

      <StatusBanner status={status} message={statusMsg} />

      <Button className="gap-1.5 bg-[#076698] hover:bg-[#055580]" onClick={handleSend} disabled={status === 'sending'}>
        {status === 'sending' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        Send Email
      </Button>
    </div>
  )
}

interface WaTemplate { name: string; params: string[]; languages: string[] }

function TemplateCombobox({ templates, loading, value, onChange }: {
  templates: WaTemplate[]
  loading: boolean
  value: string
  onChange: (name: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const filtered = templates.filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()))
  const selected = templates.find((t) => t.name === value)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !loading && setOpen((o) => !o)}
        className={cn(
          'w-full h-9 px-3 flex items-center justify-between gap-2 rounded-xl text-sm',
          'bg-slate-50 dark:bg-slate-700/50 border border-input dark:border-slate-600/60',
          'hover:bg-slate-100 dark:hover:bg-slate-700/70 focus:outline-none focus:ring-2 focus:ring-[#076698]/30 transition-colors',
          loading && 'opacity-60 cursor-not-allowed'
        )}
      >
        {loading
          ? <span className="flex items-center gap-1.5 text-muted-foreground text-xs"><Loader2 size={12} className="animate-spin" /> Loading templates…</span>
          : <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground'}>
              {selected ? selected.name : 'Select a template…'}
            </span>
        }
        <ChevronDown size={13} className="shrink-0 text-muted-foreground" />
      </button>

      {open && !loading && (
        <div className="absolute z-50 top-10 left-0 right-0 glass-card p-2 space-y-1.5">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates…"
            className="w-full h-8 px-3 rounded-lg text-xs form-field"
          />
          <div className="max-h-52 overflow-y-auto space-y-0.5">
            {filtered.length === 0
              ? <p className="text-xs text-muted-foreground px-2 py-1.5">No templates found</p>
              : filtered.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  className={cn(
                    'w-full text-left px-2 py-2 rounded-lg transition-colors',
                    'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    t.name === value && 'bg-[#076698]/10 text-[#076698] font-semibold'
                  )}
                  onClick={() => { onChange(t.name); setOpen(false); setQ('') }}
                >
                  <p className="text-xs font-medium">{t.name}</p>
                  {t.params.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Params: {t.params.join(', ')}
                    </p>
                  )}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  )
}

function WhatsAppPanel() {
  const [phone, setPhone] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [language, setLanguage] = useState('en')
  const [params, setParams] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<SendStatus>('idle')
  const [statusMsg, setStatusMsg] = useState('')
  const [templates, setTemplates] = useState<WaTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(true)
  const { open: notify } = useNotification()

  /* Load templates once */
  useEffect(() => {
    flaskApi.get<{ items: WaTemplate[] }>('/messaging/whatsapp-templates')
      .then((r) => setTemplates(r.data.items))
      .catch(() => { /* silent */ })
      .finally(() => setTemplatesLoading(false))
  }, [])

  const selectedTemplate = templates.find((t) => t.name === templateName)
  const templateParams = selectedTemplate?.params ?? []
  const availableLanguages = selectedTemplate?.languages ?? ['en', 'ar']

  const handleTemplateChange = (name: string) => {
    setTemplateName(name)
    setParams({})
    const t = templates.find((x) => x.name === name)
    if (t?.languages?.length) setLanguage(t.languages[0])
  }

  const handleSend = async () => {
    if (!phone || !templateName) {
      notify?.({ type: 'error', message: 'Phone and template are required.' })
      return
    }
    setStatus('sending')
    try {
      await flaskApi.post('/messaging/whatsapp', {
        phone,
        template_name: templateName,
        language,
        parameters: params,
      })
      setStatus('success')
      setStatusMsg(`WhatsApp message queued for ${phone}`)
      setPhone('')
      setTemplateName('')
      setParams({})
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Failed to queue WhatsApp message'
      setStatus('error')
      setStatusMsg(msg)
    }
  }

  return (
    <div className="bento-card p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
          <MessageCircle size={16} />
        </div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-700 dark:text-slate-200">Send WhatsApp</h2>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Queue a WhatsApp template message</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone Number *</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1234567890" />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Template *</label>
          <TemplateCombobox
            templates={templates}
            loading={templatesLoading}
            value={templateName}
            onChange={handleTemplateChange}
          />
        </div>

        {selectedTemplate && (
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full h-9 px-3 text-sm form-field"
            >
              {availableLanguages.map((l) => (
                <option key={l} value={l}>{l === 'en' ? 'English' : l === 'ar' ? 'Arabic' : l}</option>
              ))}
            </select>
          </div>
        )}

        {templateParams.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Template Parameters</label>
              <Info size={11} className="text-muted-foreground" />
            </div>
            {templateParams.map((paramName) => (
              <div key={paramName} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-32 shrink-0 font-mono">{paramName}</span>
                <Input
                  value={params[paramName] ?? ''}
                  onChange={(e) => setParams((p) => ({ ...p, [paramName]: e.target.value }))}
                  placeholder={`Enter ${paramName}…`}
                  className="text-xs flex-1"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <StatusBanner status={status} message={statusMsg} />

      <Button
        className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
        onClick={handleSend}
        disabled={status === 'sending'}
      >
        {status === 'sending' ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
        Send WhatsApp
      </Button>
    </div>
  )
}

export function MessagingPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Messaging</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Send one-off emails and WhatsApp messages via the platform notification queues.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <EmailPanel />
        <WhatsAppPanel />
      </div>
    </div>
  )
}
